import React, { useContext, useState } from 'react';
import { Button, Loader, Modal, TextInput } from 'is24-corecss';
import { useForm } from 'react-hook-form';
import { FetchUserCompanyDetails, UpdateUserCompanyDetails } from '@shared/api/dto/UserCompanyDetails';
import userCompanyApi from '../../../api/UserCompanyApi';
import { Common, PlaceHolder, Validation } from '../../../static/Messages';
import CountrySelectField from '../../formData/CountrySelectField';
import { MessageContext } from '../../../locale/MessageProvider';
import styles from './UserCompanyDetailsModal.module.css';
import { formValidators } from '../../../validator/FormValidators';

type UserCompanyDetailsModalProps = {
  visible: boolean;
  onClose: (reload: boolean) => void;
  value?: FetchUserCompanyDetails;
  mandatory?: boolean;
};

enum fieldName {
  name = 'name',
  email = 'email',
  phoneNumber = 'phoneNumber',
  url = 'url',
  vatNumber = 'vatNumber',
  street = 'street',
  houseNumber = 'houseNumber',
  zipCode = 'zipCode',
  city = 'city',
  country = 'country',
}

const UserCompanyDetailsModal: React.FC<UserCompanyDetailsModalProps> = ({
  visible,
  onClose,
  value,
  mandatory,
}: UserCompanyDetailsModalProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const messageFormatter = useContext(MessageContext);

  const defaultValues = { ...value };
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setError: setFieldError,
  } = useForm<FetchUserCompanyDetails>({ defaultValues });

  const onSubmitForm = () => (values: FetchUserCompanyDetails) => {
    setLoading(true);
    const _values = values as Required<FetchUserCompanyDetails>;
    const payload: UpdateUserCompanyDetails = {
      name: _values.name,
      city: _values.city,
      country: _values.country,
      email: _values.email,
      url: _values.url,
      houseNumber: _values.houseNumber,
      street: _values.street,
      vatNumber: _values.vatNumber?.length ? _values.vatNumber : undefined,
      zipCode: _values.zipCode,
      phoneNumber: _values.phoneNumber,
      phoneNumberTouched: _values.phoneNumber !== value?.phoneNumber,
    };

    (value ? userCompanyApi.update(payload) : userCompanyApi.create(payload))
      .then((data) => {
        if (typeof data === 'string') {
          return onClose(true);
        }
        if (typeof data === 'object') {
          for (const field in fieldName) {
            if (data[field]) {
              setFieldError(
                field as fieldName,
                {
                  type: 'manual',
                  message: messageFormatter.formatMessage(UserCompanyDetailsModal, 'api_validation', field),
                },
                { shouldFocus: true },
              );
            }
          }
        }
      })
      .finally(() => setLoading(false));
  };

  const onCloseWrapper = () => {
    if (visible) onClose(false);
  };

  const onCloseWrapperModal = () => {
    if (!mandatory && visible) onClose(false);
  };
  return (
    <Modal
      visible={visible}
      onClose={onCloseWrapperModal}
      title={messageFormatter.formatMessage(UserCompanyDetailsModal, Common[Common.modalName])}
      ariaLabelCloseButton={mandatory ? 'mandatory_hide' : ''}
    >
      {loading && (
        <div>
          <div className={styles.overlay}>
            <Loader />
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmitForm())} data-testid="update-company-details">
        <div className="grid grid-flex">
          <div className="grid-item one-whole padding-bottom-l">
            <TextInput
              id={fieldName.name}
              label={messageFormatter.formatMessage(UserCompanyDetailsModal, fieldName.name)}
              placeholder={messageFormatter.formatPlaceholderMessage(PlaceHolder.companyName)}
              mandatory
              errorMessage={errors.name?.message}
              valid={!errors.name}
              {...register(fieldName.name, {
                required: messageFormatter.formatValidationMessage(Validation.mandatoryCompanyName),
                validate: {
                  notEmpty: formValidators.validateNotEmptyOrMessage(
                    messageFormatter.formatValidationMessage(Validation.mandatoryCompanyName),
                  ),
                },
              })}
            />
          </div>
          <div className="grid-item seven-tenths padding-right-s padding-bottom-l">
            <TextInput
              id={fieldName.street}
              label={messageFormatter.formatMessage(UserCompanyDetailsModal, fieldName.street)}
              placeholder={messageFormatter.formatPlaceholderMessage(PlaceHolder.street)}
              mandatory
              errorMessage={errors.street?.message}
              valid={!errors.street}
              {...register(fieldName.street, {
                required: messageFormatter.formatValidationMessage(Validation.mandatoryStreet),
                validate: {
                  notEmpty: formValidators.validateNotEmptyOrMessage(
                    messageFormatter.formatValidationMessage(Validation.mandatoryStreet),
                  ),
                },
              })}
            />
          </div>
          <div className="grid-item three-tenths padding-left-s padding-bottom-l">
            <TextInput
              id={fieldName.houseNumber}
              label={messageFormatter.formatMessage(UserCompanyDetailsModal, fieldName.houseNumber)}
              placeholder={messageFormatter.formatPlaceholderMessage(PlaceHolder.houseNumber)}
              mandatory
              errorMessage={errors.houseNumber?.message}
              valid={!errors.houseNumber}
              {...register(fieldName.houseNumber, {
                required: messageFormatter.formatValidationMessage(Validation.mandatoryHouseNumber),
                validate: {
                  notEmpty: formValidators.validateNotEmptyOrMessage(
                    messageFormatter.formatValidationMessage(Validation.mandatoryHouseNumber),
                  ),
                },
              })}
            />
          </div>
          <div className="grid-item three-tenths padding-right-s padding-bottom-l">
            <TextInput
              id={fieldName.zipCode}
              label={messageFormatter.formatMessage(UserCompanyDetailsModal, fieldName.zipCode)}
              placeholder={messageFormatter.formatPlaceholderMessage(PlaceHolder.zipCode)}
              mandatory
              errorMessage={errors.zipCode?.message}
              valid={!errors.zipCode}
              {...register(fieldName.zipCode, {
                required: messageFormatter.formatValidationMessage(Validation.mandatoryZipCode),
                validate: {
                  notEmpty: formValidators.validateNotEmptyOrMessage(
                    messageFormatter.formatValidationMessage(Validation.mandatoryZipCode),
                  ),
                },
              })}
            />
          </div>
          <div className="grid-item seven-tenths padding-left-s padding-bottom-l">
            <TextInput
              id={fieldName.city}
              label={messageFormatter.formatMessage(UserCompanyDetailsModal, fieldName.city)}
              placeholder={messageFormatter.formatPlaceholderMessage(PlaceHolder.city)}
              mandatory
              errorMessage={errors.city?.message}
              valid={!errors.city}
              {...register(fieldName.city, {
                required: messageFormatter.formatValidationMessage(Validation.mandatoryCity),
                validate: {
                  notEmpty: formValidators.validateNotEmptyOrMessage(
                    messageFormatter.formatValidationMessage(Validation.mandatoryCity),
                  ),
                },
              })}
            />
          </div>
          <div className="grid-item one-whole padding-bottom-l">
            <CountrySelectField
              id={fieldName.country}
              label={messageFormatter.formatMessage(UserCompanyDetailsModal, fieldName.country)}
              errorMessage={errors.country?.message}
              register={register}
            />
          </div>
          <div className="grid-item one-whole padding-bottom-l">
            <TextInput
              id={fieldName.vatNumber}
              label={messageFormatter.formatMessage(UserCompanyDetailsModal, fieldName.vatNumber)}
              placeholder=""
              errorMessage={errors.vatNumber?.message}
              valid={!errors.vatNumber}
              {...register(fieldName.vatNumber)}
            />
          </div>
          <div className="grid-item two-fourths padding-right-s padding-bottom-l">
            <TextInput
              id={fieldName.phoneNumber}
              label={messageFormatter.formatMessage(UserCompanyDetailsModal, fieldName.phoneNumber)}
              placeholder={messageFormatter.formatPlaceholderMessage(PlaceHolder.phoneNumber)}
              mandatory
              errorMessage={errors.phoneNumber?.message}
              valid={!errors.phoneNumber}
              {...register(fieldName.phoneNumber, {
                required: messageFormatter.formatValidationMessage(Validation.mandatoryPhoneNumber),
                validate: {
                  notEmpty: formValidators.validateNotEmptyOrMessage(
                    messageFormatter.formatValidationMessage(Validation.mandatoryPhoneNumber),
                  ),
                },
              })}
            />
          </div>
          <div className="grid-item two-fourths padding-left-s padding-bottom-l">
            <TextInput
              id={fieldName.url}
              label={messageFormatter.formatMessage(UserCompanyDetailsModal, fieldName.url)}
              placeholder={messageFormatter.formatPlaceholderMessage(PlaceHolder.url)}
              mandatory
              errorMessage={errors.url?.message}
              valid={!errors.url}
              {...register(fieldName.url, {
                required: messageFormatter.formatValidationMessage(Validation.mandatoryUrl),
                validate: {
                  notEmpty: formValidators.validateNotEmptyOrMessage(
                    messageFormatter.formatValidationMessage(Validation.mandatoryUrl),
                  ),
                  urlPattern: formValidators.validateURLOrMessage(
                    messageFormatter.formatValidationMessage(Validation.incorrectUrlPattern),
                  ),
                },
              })}
            />
          </div>
          <div className="grid-item one-whole padding-bottom-l">
            <TextInput
              id={fieldName.email}
              label={messageFormatter.formatMessage(UserCompanyDetailsModal, fieldName.email)}
              placeholder={messageFormatter.formatPlaceholderMessage(PlaceHolder.email)}
              mandatory
              errorMessage={errors.email?.message}
              valid={!errors.email}
              {...register(fieldName.email, {
                required: messageFormatter.formatValidationMessage(Validation.mandatoryEmail),
                validate: {
                  notEmpty: formValidators.validateNotEmptyOrMessage(
                    messageFormatter.formatValidationMessage(Validation.mandatoryEmail),
                  ),
                  emailPattern: formValidators.validateEmailOrMessage(
                    messageFormatter.formatValidationMessage(Validation.incorrectEmailPattern),
                  ),
                },
              })}
            />
          </div>
          <div className="grid-item one-half padding-right-m">
            {mandatory && (
              <Button data-testid="cancel-button" appearance="secondary" className="one-whole" onClick={onCloseWrapper}>
                <p>{messageFormatter.formatCommonMessage(Common.back)}</p>
              </Button>
            )}
          </div>
          <div className="grid-item one-half padding-left-m">
            <Button
              data-testid="update-user-company-details-button"
              disabled={isSubmitting}
              appearance="primary"
              className="one-whole"
              onClick={handleSubmit(onSubmitForm())}
            >
              <p>{messageFormatter.formatCommonMessage(Common.save)}</p>
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default UserCompanyDetailsModal;
